import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileText, Filter, MapPin } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { IMAGES } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CITIES = ["All", "Bengaluru", "Dubai", "Global"];
const CATS = ["All", "Market Report", "Investment Thesis", "Micro-market", "Regulatory", "Whitepaper"];

const Research = () => {
  const [docs, setDocs] = useState([]);
  const [city, setCity] = useState("All");
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (city !== "All") params.city = city;
    if (cat !== "All") params.category = cat;
    axios.get(`${API}/research`, { params })
      .then((r) => setDocs(r.data))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [city, cat]);

  const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  const fmtDate = (s) => new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <>
      <Seo
        title="Research & Market Insights"
        path="/research"
        description="Download curated research reports, market memos, and investment theses across Bengaluru and Dubai real estate from the My Hoom advisory desk."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "My Hoom Research",
          "description": "Real estate research reports for Bengaluru and Dubai.",
        }}
      />

      <section className="relative bg-navy text-white py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.advisory} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
        </div>
        <div className="container-tight relative z-10 max-w-3xl">
          <Reveal>
            <span className="eyebrow text-gold">Research</span>
            <h1 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              Decisions, backed by <span className="text-gold">evidence</span>.
            </h1>
            <p className="mt-7 text-white/80 text-lg leading-relaxed">
              Market reports, micro-market deep-dives, and investment theses authored by the My Hoom advisory desk — free to download.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="container-tight">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-12">
            <div className="flex items-center gap-2 text-sm text-navy/60"><Filter size={14} /> Filter</div>
            <div className="flex flex-wrap gap-2">
              {CITIES.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-city-${c.toLowerCase()}`}
                  onClick={() => setCity(c)}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${
                    city === c ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-black/10 hover:border-navy/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <span className="text-navy/20">·</span>
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setCat(c)}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${
                    cat === c ? "bg-gold text-white border-gold" : "bg-white text-navy/70 border-black/10 hover:border-gold/50"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center text-navy/50 py-20">Loading research...</div>
          ) : docs.length === 0 ? (
            <Reveal>
              <div data-testid="research-empty" className="text-center py-20 border border-dashed border-black/10 rounded-2xl">
                <FileText className="mx-auto text-gold" size={32} strokeWidth={1.5} />
                <h3 className="mt-4 font-serif text-2xl text-navy">Library coming soon.</h3>
                <p className="mt-2 text-navy/60 text-sm max-w-md mx-auto">
                  Our advisory desk is preparing the next set of market memos. Until then, write to us for a tailored brief.
                </p>
                <Link to="/contact" className="btn-gold mt-7 text-sm">Request a tailored memo <ArrowRight size={14} /></Link>
              </div>
            </Reveal>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {docs.map((d, i) => (
                <Reveal key={d.id} delay={i * 0.05}>
                  <article data-testid={`research-card-${d.id}`} className="card-lift p-7 border border-black/5 h-full flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] tracking-[0.22em] uppercase text-gold font-semibold">{d.category || "Report"}</span>
                      {d.city && (
                        <span className="text-[11px] flex items-center gap-1 text-navy/55"><MapPin size={11} />{d.city}</span>
                      )}
                    </div>
                    <h3 className="mt-4 font-serif text-2xl text-navy leading-tight">{d.title}</h3>
                    {d.summary && <p className="mt-3 text-sm text-navy/65 leading-relaxed line-clamp-3">{d.summary}</p>}
                    <div className="mt-5 flex items-center gap-3 text-xs text-navy/50">
                      <FileText size={14} />
                      <span>{(d.original_filename || "").split('.').pop().toUpperCase()}</span>
                      <span>·</span>
                      <span>{fmtSize(d.size || 0)}</span>
                      <span>·</span>
                      <span>{fmtDate(d.created_at)}</span>
                    </div>
                    <a
                      href={`${API}/research/${d.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`research-download-${d.id}`}
                      className="btn-navy mt-6 text-sm w-full"
                    >
                      <Download size={14} /> Download
                    </a>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Research;
